/**
 * PushNotificationBanner - Prompts drivers to enable push notifications
 * CRITICAL: Keep this component simple and working
 */
import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const VAPID_PUBLIC_KEY = "BHmYQFvAwG77BMoHrLCVMUQC1VVgaF0WCKc2i8QsN5J5a7IJXQufq2TJ3y0744KnjJbfvpBNk8RidovWEv2jUIE";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationBanner() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only show if notifications are supported and not already granted
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      console.log("Push not supported");
      return;
    }
    
    if (localStorage.getItem("push_enabled") === "true") {
      return;
    }
    
    if (Notification.permission === "denied") {
      return;
    }
    
    setShow(true);
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log("Step 1: Requesting permission...");
      const permission = await Notification.requestPermission();
      console.log("Permission result:", permission);
      
      if (permission !== "granted") {
        toast.error("Please allow notifications in your browser settings");
        setShow(false);
        return;
      }

      console.log("Step 2: Getting service worker...");
      const registration = await navigator.serviceWorker.ready;
      console.log("SW ready:", registration);

      console.log("Step 3: Subscribing to push...");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      console.log("Subscription:", subscription);

      // Get keys
      const p256dh = subscription.getKey("p256dh");
      const auth = subscription.getKey("auth");
      
      const keys = {
        p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh))),
        auth: btoa(String.fromCharCode.apply(null, new Uint8Array(auth)))
      };
      console.log("Keys prepared");

      console.log("Step 4: Sending to backend...");
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_BACKEND_URL || "";
      
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: keys
        })
      });

      console.log("Response status:", response.status);
      
      if (response.ok) {
        localStorage.setItem("push_enabled", "true");
        toast.success("🔔 Notifications enabled! You'll be alerted for new packages.");
        setShow(false);
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || data.error?.message || "Server error");
      }
    } catch (err) {
      console.error("Push error:", err);
      setError(err.message);
      toast.error("Failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="bg-gradient-to-r from-[#00A86B] to-[#008B5A] p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-white" />
          <span className="text-white text-sm font-medium">
            Get alerts when new packages are available!
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleEnable}
            disabled={loading}
            size="sm"
            className="bg-white text-[#00A86B] hover:bg-gray-100 text-xs px-3"
          >
            {loading ? "Enabling..." : "Enable Alerts"}
          </Button>
          <button onClick={handleDismiss} className="text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {error && <p className="text-red-200 text-xs mt-1">{error}</p>}
    </div>
  );
}
